/**
 * Compact designation list — the "master" side of the Finance tab's
 * master-detail layout.
 *
 * One collapsible group per section (R&D, NPI, …) with the ordered
 * subtotal always visible in the group header. Rows show only the
 * key columns: name, status, ordered amount, invoiced total (for
 * invoice readers), declared progress (quick-editable) and the
 * spent/sold hours ratio (R&D only). Everything else lives in the
 * detail panel, opened by clicking the row.
 *
 * Collapse state is a local display preference — kept in
 * sessionStorage (per project), never in Firestore.
 */

import { useState, type ReactNode } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import type { Designation } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import type { DesignationGroup } from './designations';
import { formatCurrencyEUR, formatNumber } from './finance';

const SELECTED_ROW_BG = 'rgba(255, 250, 94, 0.3)';

export default function DesignationList({
  groups,
  projectId,
  locale,
  selectedId,
  onSelect,
  showInvoiced,
  invoicedByDesignation,
  renderProgressCell,
}: {
  groups: readonly DesignationGroup[];
  projectId: string;
  locale: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  showInvoiced: boolean;
  invoicedByDesignation: ReadonlyMap<string, number>;
  renderProgressCell: (d: Designation) => ReactNode;
}) {
  const { t } = useTranslation();

  const storageKey = `finance-collapsed-${projectId}`;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem(storageKey) ?? '{}');
    } catch {
      return {};
    }
  });
  const toggleSection = (section: string) => {
    setCollapsed((prev) => {
      const next = { ...prev, [section]: !prev[section] };
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage unavailable (private mode) — collapse still works in-memory.
      }
      return next;
    });
  };

  return (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
      {groups.map((group) => {
        const isCollapsed = Boolean(collapsed[group.section]);
        const subtotal = group.items.reduce((acc, d) => acc + (d.orderedAmount ?? 0), 0);
        // Band-level metrics. `hoursSold` and `hoursSpent` have different
        // scopes: only an hourly line sells hours, but time can really be
        // logged against a fixed-price one (p26014 carries 7.78 h on three
        // `fixed` lines in production). So Σhp spans every item and Σhv only
        // the hourly ones; the declared-progress average spans EVERY
        // designation in the section, a missing `progress` counting as 0%.
        const hourlyItems = group.items.filter((d) => d.billingMode === 'hourly');
        const totalHp = group.items.reduce((acc, d) => acc + (d.hoursSpent ?? 0), 0);
        const totalHv = hourlyItems.reduce((acc, d) => acc + (d.hoursSold ?? 0), 0);
        const showBandMetrics = hourlyItems.length > 0 || totalHp > 0;

        // The hours column belongs to R&D — plus any section that turns out to
        // carry hours, otherwise those hours have nowhere to show.
        const showHours = group.section === 'rd' || totalHp > 0;
        const avgProgress = group.items.length
          ? Math.round(
              group.items.reduce((acc, d) => acc + (d.progress ?? 0), 0) / group.items.length,
            )
          : 0;
        return (
          <Paper
            key={group.section}
            variant="outlined"
            sx={{ borderColor: colors.lightGray }}
            data-testid={`finance-section-${group.section}`}
          >
            <Box
              onClick={() => toggleSection(group.section)}
              data-testid={`finance-section-toggle-${group.section}`}
              sx={{
                px: 1.5,
                py: 1,
                backgroundColor: colors.peach,
                borderBottom: isCollapsed ? 'none' : `1px solid ${colors.lightGray}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: colors.warmBeige },
              }}
            >
              <IconButton size="small" sx={{ p: 0.25 }} disableRipple>
                {isCollapsed ? (
                  <ChevronRightIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                {t(`projects.designations.sections.${group.section}`)}
                <Chip
                  size="small"
                  label={group.items.length}
                  sx={{ ml: 1 }}
                  data-testid={`finance-section-count-${group.section}`}
                />
              </Typography>
              {showBandMetrics && (
                <Box
                  // Hidden below `sm` so the band stays readable on mobile —
                  // title + ordered subtotal remain visible at every width.
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 1.5,
                    mr: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(avgProgress, 100)}
                      data-testid={`finance-section-progress-${group.section}`}
                      sx={{
                        width: 64,
                        height: 6,
                        borderRadius: 3,
                        flexShrink: 0,
                        backgroundColor: colors.lightGray,
                        '& .MuiLinearProgress-bar': { backgroundColor: colors.yellow },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: colors.textMuted, minWidth: 30 }}
                    >
                      {avgProgress}%
                    </Typography>
                  </Box>
                  <Tooltip title={t('finance.columns.hours')}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.textMuted, whiteSpace: 'nowrap' }}
                      data-testid={`finance-section-hours-${group.section}`}
                    >
                      {formatNumber(totalHp, locale)} / {formatNumber(totalHv, locale)} h
                    </Typography>
                  </Tooltip>
                </Box>
              )}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700 }}
                data-testid={`finance-section-subtotal-${group.section}`}
              >
                {formatCurrencyEUR(subtotal, locale)}
              </Typography>
            </Box>
            <Collapse in={!isCollapsed} unmountOnExit>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 160 }}>
                        {t('finance.columns.name')}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {t('finance.columns.orderedAmount')}
                      </TableCell>
                      {showInvoiced && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {t('finance.columns.alreadyInvoiced')}
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 170 }}>
                        {t('finance.columns.progress')}
                      </TableCell>
                      {showHours && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 90 }}>
                          {/* Abbreviated so the column stays narrow when the
                              detail panel compresses the list — full label
                              in the tooltip. */}
                          <Tooltip title={t('finance.columns.hours')}>
                            <span>{t('finance.columns.hoursShort')}</span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.items.map((d) => (
                      <TableRow
                        key={d.id}
                        hover
                        onClick={() => onSelect(d.id)}
                        selected={selectedId === d.id}
                        data-testid={`finance-row-${d.id}`}
                        sx={{
                          cursor: 'pointer',
                          // Archived rows read as struck-through and faded —
                          // the same signal Clockify uses on a done task.
                          opacity: d.isArchived ? 0.5 : 1,
                          '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: SELECTED_ROW_BG,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              textDecoration: d.isArchived ? 'line-through' : 'none',
                            }}
                          >
                            {d.name}
                          </Typography>
                          {d.category ? (
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>
                              {d.category}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {formatCurrencyEUR(d.orderedAmount ?? 0, locale)}
                        </TableCell>
                        {showInvoiced && (
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                            data-testid={`finance-invoiced-${d.id}`}
                          >
                            {formatCurrencyEUR(invoicedByDesignation.get(d.id) ?? 0, locale)}
                          </TableCell>
                        )}
                        <TableCell
                          // Quick-edit cell: clicks must not toggle row selection.
                          onClick={(e) => e.stopPropagation()}
                          sx={{ cursor: 'default' }}
                        >
                          {renderProgressCell(d)}
                        </TableCell>
                        {showHours && (
                          <TableCell
                            align="right"
                            sx={{ whiteSpace: 'nowrap' }}
                            data-testid={`finance-hours-${d.id}`}
                          >
                            <Typography component="span" variant="body2">
                              {formatNumber(d.hoursSpent, locale)}
                            </Typography>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ color: colors.textMuted }}
                            >
                              {' / '}
                              {formatNumber(d.hoursSold, locale)} h
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}
